import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useAtomValue } from 'jotai'
import BloodlabLogo from '../assets/bloodlab-logo.svg'
import HolterDoDomuLogo from '../assets/holter-do-domu-logo.png'
import { BloodlabInterpretationLink, HolterDoDomuLink } from '../const'
import { GetAvailableLaboratoriesSubscription } from '../generated/urql.client'
import { StorageUrl } from '../const'
import { AuthContext } from '../provider/auth'
import { BasketContext } from '../provider/basket'
import { Frame } from '../components/Frame'
import { TestCategories } from '../components/TestCategories'
import {
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom'
import IconInput from '../components/IconInput'
import { IoMdSearch } from 'react-icons/io'
import { IoClose } from 'react-icons/io5'
import { useDebouncedCallback } from 'use-debounce'
import { TailSpin } from 'react-loader-spinner'
import { Blue300 } from '../lib/colors'
import { useI18n } from '../provider/i18n'
import { ConfirmDialog, ConfirmDialogType } from '../components/ConfirmDialog'
import {
  availableLaboratoriesSubscriptionAtom,
  availableTestTagsSubscriptionAtom,
  profilesSubscriptionAtom,
} from '../store/subscription.atoms'
import { isShowHolterAndBloodlabEnabledAtom } from '../store/featureFlags.atoms'

export const CatalogPage = () => {
  const basket = useContext(BasketContext)
  const auth = useContext(AuthContext)
  const i18n = useI18n()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [searchInput, setSearchInput] = useState(searchParams.get('q') || '')
  const confirmDialogRef = useRef<ConfirmDialogType>(null)
  const [{ data: profilesData }] = useAtomValue(profilesSubscriptionAtom)
  const [
    {
      data: availableLaboratoriesData,
      fetching: availableLaboratoriesFetching,
    },
  ] = useAtomValue(availableLaboratoriesSubscriptionAtom)
  const [{ data: availableTestTagsData }] = useAtomValue(
    availableTestTagsSubscriptionAtom
  )
  const isShowHolterAndBloodlabEnabled = useAtomValue(
    isShowHolterAndBloodlabEnabledAtom
  )

  const isTestTagsAvailable = useMemo(
    () => !!availableTestTagsData?.available_test_tag.length,
    [availableTestTagsData?.available_test_tag.length]
  )

  const isSearchVisible = useMemo(() => {
    return (
      location.pathname === '/' ||
      location.pathname.startsWith('/tag') ||
      location.pathname.startsWith('/search')
    )
  }, [location])

  const isTagsVisible = useMemo(() => {
    return (
      isTestTagsAvailable &&
      (location.pathname === '/' ||
        location.pathname.startsWith('/tag') ||
        location.pathname.startsWith('/test'))
    )
  }, [location, isTestTagsAvailable])

  const isTagsVisibleScreenSm = useMemo(() => {
    return (
      (isTestTagsAvailable && location.pathname === '/') ||
      location.pathname.startsWith('/tag')
    )
  }, [location])

  const setSearchQuery = useDebouncedCallback((query: string) => {
    if (query) {
      navigate(`/search?q=${encodeURIComponent(query)}`)
    } else {
      navigate(`/`)
    }
  }, 300)

  useEffect(() => {
    if (!basket.profile && profilesData?.client_profile.length) {
      basket.setProfile(profilesData?.client_profile[0])
    }
  }, [basket, profilesData])

  useEffect(() => {
    if (auth.accessToken) {
      return
    }

    if (basket.overrideAddress) {
      return
    }
  }, [auth.accessToken, basket.overrideAddress])

  const availableLaboratoriesWithLogo = useMemo(
    () =>
      (availableLaboratoriesData?.available_laboratory || []).filter(
        (
          laboratory
        ): laboratory is GetAvailableLaboratoriesSubscription['available_laboratory'][number] & {
          logoFile: { id: number }
        } => !!laboratory.logoFile
      ),
    [availableLaboratoriesData]
  )

  return (
    <Frame
      leftClassName="w-[210px] hidden md:block"
      rightClassName="xl:w-[210px] md:hidden xl:block"
      renderLeft={isTagsVisible && <TestCategories />}
      renderRight={
        isTestTagsAvailable &&
        basket.address?.location && (
          <>
            <div className="flex items-center gap-1 mb-5">
              <p className="text-blue-700">
                {i18n.t('screens.catalog.partners.title')}
              </p>
            </div>
            <ul className="flex flex-row md:flex-col gap-5 overflow-x-scroll md:overflow-visible p-5 -m-5">
              {isShowHolterAndBloodlabEnabled && (
                <>
                  <li
                    className="hl-panel h-24 flex items-center justify-center cursor-pointer min-w-40"
                    onClick={() => {
                      confirmDialogRef.current?.show({
                        title: i18n.t('screens.catalog.bloodlab.dialog.title'),
                        message: i18n.t(
                          'screens.catalog.bloodlab.dialog.message'
                        ),
                        successText: i18n.t(
                          'screens.catalog.bloodlab.dialog.button.ok'
                        ),
                        cancelText: i18n.t(
                          'screens.catalog.bloodlab.dialog.button.learnMore'
                        ),
                        onCancel: () => {
                          window.open(BloodlabInterpretationLink, '_blank')
                        },
                      })
                    }}
                  >
                    <img
                      src={BloodlabLogo}
                      alt="Bloodlab"
                      width={136}
                      height={76}
                    />
                  </li>
                  <li
                    className="hl-panel flex items-center justify-center cursor-pointer min-w-40 h-24"
                    onClick={() => window.open(HolterDoDomuLink, '_blank')}
                  >
                    <img
                      src={HolterDoDomuLogo}
                      alt="Holter Do Domu"
                      width={136}
                      height={76}
                    />
                  </li>
                </>
              )}
              {availableLaboratoriesFetching ? (
                <TailSpin
                  wrapperClass="flex justify-center items-center"
                  color={Blue300}
                  height={24}
                  width={24}
                />
              ) : (
                availableLaboratoriesWithLogo.map((laboratory) => (
                  <li
                    key={laboratory.id}
                    className="hl-panel flex items-center justify-center min-w-40 h-24"
                  >
                    {laboratory.url ? (
                      <a
                        href={laboratory.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          alt={laboratory.name}
                          width={136}
                          height={76}
                          src={`${StorageUrl}/files/${laboratory.logoFile.id}`}
                        />
                      </a>
                    ) : (
                      <img
                        alt={laboratory.name}
                        width={136}
                        height={76}
                        src={`${StorageUrl}/files/${laboratory.logoFile.id}`}
                      />
                    )}
                  </li>
                ))
              )}
            </ul>
          </>
        )
      }
    >
      {isSearchVisible && (
        <div>
          <IconInput
            leftIcon={<IoMdSearch size={24} color="#000" />}
            rightIcon={
              <IoClose
                size={24}
                color="#000"
                className={searchInput ? 'cursor-pointer' : 'hidden'}
                onClick={() => {
                  setSearchInput('')
                  setSearchQuery('')
                }}
              />
            }
            placeholder={i18n.t('screens.catalog.search.placeholder')}
            onChange={(e) => {
              setSearchInput(e.target.value)
              setSearchQuery(e.target.value)
            }}
            value={searchInput}
          />
        </div>
      )}
      {isTagsVisibleScreenSm && (
        <div className="md:hidden mt-5">
          <TestCategories />
        </div>
      )}
      <Outlet />
      <ConfirmDialog ref={confirmDialogRef} />
    </Frame>
  )
}
